import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { hamtaDokument, harDokument } from '../instruktionerApi';
import PappersSkelett from './PappersSkelett';
import './dokumentVisare.css';
import './delat.css';

const BREDD = 860; // instruktionens A4-sidor (794 px) + marginal
const A4_HOJD = 1137;

// Instruktionerna har inga skript. Ramen saknar därför allow-scripts, men har
// samma ursprung så att sidan kan hoppa till kapitel och markera sökträffar.
const SANDBOX = 'allow-same-origin allow-popups allow-popups-to-escape-sandbox';
const INJICERAD_STIL = `
  html, body { background: transparent !important; margin: 0 !important; }
  body { padding-bottom: 14px; }
  .page { margin: 14px auto !important; }
  mark.kp-traff { background: #DBEAFE; color: inherit; border-radius: 2px; padding: 0 1px; }
  mark.kp-traff.aktiv { background: #93C5FD; box-shadow: 0 0 0 2px #3B82F6; }
`;

function rensaMarkeringar(doc) {
  doc.querySelectorAll('mark.kp-traff').forEach((m) => {
    m.replaceWith(doc.createTextNode(m.textContent));
  });
  doc.body.normalize();
}

function markera(doc, fraga) {
  rensaMarkeringar(doc);
  const q = fraga.trim().toLowerCase();
  if (q.length < 2) return [];
  const vandrare = doc.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT, {
    acceptNode: (n) => (n.parentElement && /^(SCRIPT|STYLE)$/.test(n.parentElement.tagName)
      ? NodeFilter.FILTER_REJECT : NodeFilter.FILTER_ACCEPT),
  });
  const noder = [];
  while (vandrare.nextNode()) noder.push(vandrare.currentNode);
  const markeringar = [];
  noder.forEach((nod) => {
    let rest = nod;
    let i = rest.nodeValue.toLowerCase().indexOf(q);
    while (i >= 0) {
      const traff = rest.splitText(i);
      rest = traff.splitText(q.length);
      const m = doc.createElement('mark');
      m.className = 'kp-traff';
      traff.replaceWith(m);
      m.appendChild(traff);
      markeringar.push(m);
      i = rest.nodeValue.toLowerCase().indexOf(q);
    }
  });
  return markeringar;
}

/**
 * Visar en instruktion: hämtning med pappersskelett och förloppsindikator,
 * skalning till kolumnens bredd, kapitelhopp, sökmarkering och sidindikator.
 * Ref: { gaTillSida(n), sok(fraga) => antal, nastaTraff() }.
 */
const DokumentVisare = forwardRef(function DokumentVisare(
  { nyckel, sidor = 1, titel = '', startSida = 1, visaSok = true, tips = '', onSida },
  ref,
) {
  const ytaRef = useRef(null);
  const ramRef = useRef(null);
  const sidElRef = useRef([]);
  const traffRef = useRef({ lista: [], index: -1 });
  const skalaRef = useRef(1);
  const [html, setHtml] = useState(null);
  const [fel, setFel] = useState(null);
  const [forsok, setForsok] = useState(0);
  const [forlopp, setForlopp] = useState(0);
  const [laddar, setLaddar] = useState(true);
  const [skelett, setSkelett] = useState(false);
  const [direkt, setDirekt] = useState(false);
  const [skala, setSkala] = useState(1);
  const [hojd, setHojd] = useState(A4_HOJD + 14);
  const [sida, setSida] = useState(1);
  const [sokText, setSokText] = useState('');
  const [traffInfo, setTraffInfo] = useState({ antal: 0, index: -1 });

  // Hämtning: skelett om det tar mer än 150 ms, och då i minst 400 ms så att det inte blinkar.
  useEffect(() => {
    let avbruten = false;
    setFel(null);
    setSokText('');
    setTraffInfo({ antal: 0, index: -1 });
    if (harDokument(nyckel)) {
      setDirekt(true);
      setLaddar(true);
      hamtaDokument(nyckel).then((h) => { if (!avbruten) setHtml(h); });
      return () => { avbruten = true; };
    }
    setDirekt(false);
    setLaddar(true);
    setHtml(null);
    setForlopp(0);
    let visadVid = null;
    const timer = setTimeout(() => { setSkelett(true); visadVid = Date.now(); }, 150);
    hamtaDokument(nyckel, (p) => { if (!avbruten) setForlopp(p); })
      .then((h) => {
        if (avbruten) return;
        clearTimeout(timer);
        setForlopp(100);
        const vanta = visadVid ? Math.max(0, 400 - (Date.now() - visadVid)) : 0;
        setTimeout(() => { if (!avbruten) setHtml(h); }, vanta);
      })
      .catch((e) => { if (!avbruten) { clearTimeout(timer); setFel(e.message || 'Instruktionen kunde inte hämtas.'); } });
    return () => { avbruten = true; clearTimeout(timer); };
  }, [nyckel, forsok]);

  // Skala till kolumnens bredd.
  useEffect(() => {
    const yta = ytaRef.current;
    if (!yta) return undefined;
    const ro = new ResizeObserver(() => {
      const k = Math.min(1.25, yta.clientWidth / BREDD) || 1;
      skalaRef.current = k;
      setSkala(k);
    });
    ro.observe(yta);
    return () => ro.disconnect();
  }, []);

  const gaTillSida = useCallback((n) => {
    const el = sidElRef.current[Math.max(0, (n || 1) - 1)];
    const yta = ytaRef.current;
    if (!el || !yta) return;
    yta.scrollTo({ top: Math.max(0, (el.offsetTop - 8) * skalaRef.current), behavior: 'smooth' });
  }, []);

  const visaTraff = useCallback((index) => {
    const { lista } = traffRef.current;
    if (!lista.length) return;
    const i = ((index % lista.length) + lista.length) % lista.length;
    lista.forEach((m, j) => m.classList.toggle('aktiv', j === i));
    traffRef.current.index = i;
    setTraffInfo({ antal: lista.length, index: i });
    const doc = ramRef.current && ramRef.current.contentDocument;
    const topp = lista[i].getBoundingClientRect().top + ((doc && doc.documentElement.scrollTop) || 0);
    ytaRef.current.scrollTo({ top: Math.max(0, (topp - 120) * skalaRef.current), behavior: 'smooth' });
  }, []);

  const sok = useCallback((fraga) => {
    const doc = ramRef.current && ramRef.current.contentDocument;
    if (!doc || !doc.body) return 0;
    const lista = markera(doc, fraga || '');
    traffRef.current = { lista, index: -1 };
    setTraffInfo({ antal: lista.length, index: -1 });
    if (lista.length) visaTraff(0);
    return lista.length;
  }, [visaTraff]);

  useImperativeHandle(ref, () => ({
    gaTillSida,
    sok,
    nastaTraff: () => visaTraff(traffRef.current.index + 1),
  }), [gaTillSida, sok, visaTraff]);

  const nyttSokText = (v) => { setSokText(v); sok(v); };

  const onLoad = () => {
    const doc = ramRef.current && ramRef.current.contentDocument;
    if (!doc || !html) return;
    const stil = doc.createElement('style');
    stil.textContent = INJICERAD_STIL;
    doc.head.appendChild(stil);
    if (!doc.querySelector('base')) {
      const bas = doc.createElement('base');
      bas.target = '_blank';
      doc.head.prepend(bas);
    }
    sidElRef.current = Array.from(doc.querySelectorAll('section.page'));
    setHojd(doc.documentElement.scrollHeight);
    setLaddar(false);
    setSkelett(false);
    setSida(1);
    if (onSida) onSida(1);
    requestAnimationFrame(() => {
      setDirekt(false);
      if (startSida > 1) gaTillSida(startSida);
    });
  };

  const onScroll = () => {
    const yta = ytaRef.current;
    const topp = yta.scrollTop / skalaRef.current + 60;
    let n = 1;
    sidElRef.current.forEach((el, i) => { if (el.offsetTop <= topp) n = i + 1; });
    if (n !== sida) { setSida(n); if (onSida) onSida(n); }
  };

  const antalSidor = sidElRef.current.length || sidor;
  const visaSkelett = laddar && !direkt;

  return (
    <div className="instr-doc">
      <div className="instr-doc-bar">
        {visaSok ? (
          <label className="instr-doc-sok">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" /></svg>
            <input
              value={sokText}
              onChange={(e) => nyttSokText(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); visaTraff(traffRef.current.index + (e.shiftKey ? -1 : 1)); } }}
              placeholder="Sök i instruktionen"
              aria-label="Sök i instruktionen"
              disabled={laddar}
            />
          </label>
        ) : <span>{tips}</span>}
        {visaSok && sokText.trim().length >= 2 && (
          <span className="instr-doc-traffar" aria-live="polite">
            {traffInfo.antal
              ? <>{traffInfo.index + 1} av {traffInfo.antal} <button type="button" onClick={() => visaTraff(traffInfo.index + 1)}>Nästa</button></>
              : 'Inga träffar'}
          </span>
        )}
        <span className="instr-doc-sida">
          {laddar && !fel
            ? <span className="instr-doc-status"><span className="instr-doc-punkt" />Hämtar instruktionen</span>
            : `Sida ${sida} av ${antalSidor}`}
        </span>
      </div>
      <div className="instr-doc-yta" ref={ytaRef} onScroll={onScroll}>
        <div className="instr-doc-forlopp" aria-hidden="true">
          <i style={{ width: `${forlopp}%`, opacity: visaSkelett && forlopp < 100 ? 1 : 0 }} />
        </div>
        {fel ? (
          <div className="instr-doc-fel" role="alert">
            <p>{fel}</p>
            <button type="button" className="instr-btn instr-btn--outline" onClick={() => setForsok((n) => n + 1)}>Försök igen</button>
          </div>
        ) : (
          <div style={{ height: (laddar ? A4_HOJD + 14 : hojd) * skala, overflow: 'hidden' }}>
            <div
              className={`instr-doc-skala${laddar ? ' laddar' : ''}${direkt ? ' direkt' : ''}`}
              style={{ transform: `scale(${skala})` }}
            >
              {skelett && <PappersSkelett />}
              {html && (
                <iframe
                  ref={ramRef}
                  title={`Instruktion ${titel}`}
                  sandbox={SANDBOX}
                  srcDoc={html}
                  onLoad={onLoad}
                  style={{ height: hojd }}
                />
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
});

export default DokumentVisare;
